import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from './ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import { Download, Loader2, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API_URL } from '../lib/apiBase';
import { fullName } from '../lib/fullName';

const getHeaders = () => {
  const token = localStorage.getItem('hrms-token');
  return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
};

const STATUS_COLOR = {
  'Present': 'text-emerald-700 bg-emerald-50',
  'On Leave': 'text-blue-700 bg-blue-50',
  'Absent': 'text-red-700 bg-red-50',
  'Week Off': 'text-slate-500 bg-slate-100',
  'Holiday': 'text-purple-700 bg-purple-50',
  'Upcoming': 'text-slate-300',
};

export const MonthlyAttendanceReport = ({ entityId, currentUser }) => {
  const isPriv = ['super_admin', 'admin', 'hr', 'principal', 'hod', 'branch_admin', 'dean'].includes(currentUser?.role);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [employees, setEmployees] = useState([]);
  const [empId, setEmpId] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!isPriv) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/employees${entityId ? `?entity_id=${entityId}` : ''}`, { headers: getHeaders(), credentials: 'include' });
        if (res.ok) setEmployees(await res.json());
      } catch { /* ignore */ }
    })();
  }, [isPriv, entityId]);

  const fetchReport = useCallback(async () => {
    if (isPriv && !empId) { toast.error('Select an employee'); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ month });
      if (isPriv && empId) params.append('employee_id', empId);
      const res = await fetch(`${API_URL}/api/attendance/monthly-report?${params}`, { headers: getHeaders(), credentials: 'include' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed to load report');
      setReport(d);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [month, empId, isPriv]);

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams({ month, format: 'excel' });
      if (isPriv && empId) params.append('employee_id', empId);
      const res = await fetch(`${API_URL}/api/attendance/monthly-report?${params}`, { headers: getHeaders(), credentials: 'include' });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendance_${report?.employee_id || 'me'}_${month}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
    finally { setDownloading(false); }
  };

  const s = report?.summary || {};

  return (
    <div className="space-y-4" data-testid="monthly-attendance-report">
      <div className="flex flex-col md:flex-row gap-3 md:items-end">
        {isPriv && (
          <div className="space-y-1.5 flex-1">
            <Label>Employee</Label>
            <Select value={empId} onValueChange={setEmpId}>
              <SelectTrigger data-testid="mar-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent>
                {employees.map(e => (
                  <SelectItem key={e.employee_id} value={e.employee_id}>
                    {fullName(e)} — {e.department || ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="space-y-1.5">
          <Label>Month</Label>
          <Input type="month" value={month} onChange={e => setMonth(e.target.value)} data-testid="mar-month" />
        </div>
        <Button onClick={fetchReport} disabled={loading} data-testid="mar-load-btn">
          {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CalendarDays className="w-4 h-4 mr-1.5" />}
          View Report
        </Button>
        {report && (
          <Button variant="outline" onClick={downloadExcel} disabled={downloading} data-testid="mar-download-btn">
            {downloading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
            Excel
          </Button>
        )}
      </div>

      {report && (
        <>
          <div className="flex flex-wrap gap-2" data-testid="mar-summary">
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Present: {s.present || 0}</Badge>
            <Badge className="bg-amber-100 text-amber-700 border-amber-200">Half Day: {s.half_day || 0}</Badge>
            <Badge className="bg-orange-100 text-orange-700 border-orange-200">Late: {s.late || 0}</Badge>
            <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Early Out: {s.early_out || 0}</Badge>
            <Badge className="bg-pink-100 text-pink-700 border-pink-200">Missed Punch-Out: {s.missed_out || 0}</Badge>
            <Badge className="bg-blue-100 text-blue-700 border-blue-200">On Leave: {s.on_leave || 0}</Badge>
            <Badge className="bg-red-100 text-red-700 border-red-200">Absent: {s.absent || 0}</Badge>
            <Badge variant="outline" className="border-slate-300 text-slate-500">Week Off: {s.week_off || 0}</Badge>
            <Badge variant="outline" className="border-purple-300 text-purple-600">Holiday: {s.holiday || 0}</Badge>
          </div>

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Day</TableHead>
                      <TableHead className="text-center">In Time</TableHead>
                      <TableHead className="text-center">Out Time</TableHead>
                      <TableHead className="text-center">Hours</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Reason / Remarks</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.rows.map(r => (
                      <TableRow key={r.date} data-testid={`mar-row-${r.date}`}
                        className={r.status === 'Week Off' || r.status === 'Upcoming' ? 'opacity-50' : ''}>
                        <TableCell className="text-xs font-medium">{r.date}</TableCell>
                        <TableCell className="text-xs text-slate-500">{r.day}</TableCell>
                        <TableCell className={`text-center text-xs ${!r.check_in && r.status !== 'Upcoming' && !['On Leave', 'Week Off', 'Holiday'].includes(r.status) ? 'text-red-500 font-semibold' : ''}`}>
                          {r.check_in || '—'}
                        </TableCell>
                        <TableCell className={`text-center text-xs ${r.check_in && !r.check_out ? 'text-red-500 font-semibold' : ''}`}>
                          {r.check_out || '—'}
                        </TableCell>
                        <TableCell className="text-center text-xs">{r.work_hours != null ? r.work_hours : '—'}</TableCell>
                        <TableCell>
                          <span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLOR[r.status] || 'text-slate-600 bg-slate-50'}`}>{r.status}</span>
                        </TableCell>
                        <TableCell className="text-xs text-slate-600">{r.reason || ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
      {!report && !loading && (
        <p className="text-sm text-slate-400 text-center py-10">
          {isPriv ? 'Select an employee and month, then click View Report' : 'Pick a month and click View Report to see your day-by-day attendance'}
        </p>
      )}
    </div>
  );
};
